import { ChangelogList } from "@/components/changelog/changelog-list"
import { Button } from "@/components/ui/button"
import type { ChangelogEntry } from "@/types/changelog"
import { Link } from "@inertiajs/react"
import { ArrowRight } from "lucide-react"
import type { ReactNode } from "react"

interface ChangelogPageShellProps {
  hero: ReactNode;
  entries: ChangelogEntry[];
  groupedEntries: Record<string, ChangelogEntry[]>;
  emptyStateMessage?: string;
  footer?: ReactNode;
}

function DefaultFooter() {
  return (
    <div className="relative overflow-hidden border-t border-amber-200 dark:border-amber-900">
      <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-amber-100/50 to-amber-50 dark:from-amber-950/30 dark:via-amber-900/20 dark:to-amber-950/30" />
      <div className="absolute -top-20 -left-20 h-40 w-40 rounded-full bg-amber-400/20 blur-3xl dark:bg-amber-600/20" />
      <div className="absolute -right-20 -bottom-20 h-60 w-60 rounded-full bg-amber-300/20 blur-3xl dark:bg-amber-700/20" />

      <div className="relative mx-auto max-w-6xl px-6 py-20 text-center">
        <h2 className="mb-6 bg-gradient-to-r from-amber-600 to-amber-700 bg-clip-text text-4xl text-transparent sm:text-5xl dark:from-amber-400 dark:to-amber-500">
          Ready to boost productivity?
        </h2>
        <p className="mb-10 text-lg text-amber-800 sm:text-xl dark:text-amber-200">
          Join Antiwork, Gemnote, and other teams saving hours every week.
        </p>
        <div className="flex flex-col flex-col-reverse items-start gap-4 sm:flex-row sm:justify-center">
          <div className="flex flex-col gap-4 self-center">
            <Button
              size="lg"
              asChild
              className="bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25 hover:from-amber-600 hover:to-amber-700 hover:shadow-xl hover:shadow-amber-500/30 dark:from-amber-500 dark:to-amber-600 dark:shadow-amber-600/20 dark:hover:from-amber-400 dark:hover:to-amber-500"
            >
              <Link href="/sign_in">
                Connect GitHub & Subscribe
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-amber-700 dark:text-amber-300">Setup in 5 minutes</p>
          </div>
          <Button
            size="lg"
            variant="outline"
            className="w-full border-amber-300 bg-white/50 text-amber-700 hover:border-amber-400 hover:bg-amber-50 sm:w-auto dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-300 dark:hover:border-amber-600 dark:hover:bg-amber-900/50"
            asChild
          >
            <Link href="/#pricing">View Pricing</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ChangelogPageShell({
  hero,
  entries,
  groupedEntries,
  emptyStateMessage = "No changelog entries yet. Check back soon for updates!",
  footer,
}: ChangelogPageShellProps) {
  return (
    <div className="bg-background text-foreground min-h-screen">
      <div className="border-border from-muted/50 to-background border-b bg-gradient-to-b">
        <div className="mx-auto max-w-6xl px-6 py-12 sm:py-16">{hero}</div>
      </div>

      {entries.length > 0 ? (
        <ChangelogList groupedEntries={groupedEntries} />
      ) : (
        <div className="mx-auto max-w-6xl px-6 py-16 text-center">
          <p className="text-muted-foreground text-lg">{emptyStateMessage}</p>
        </div>
      )}

      {footer ?? <DefaultFooter />}
    </div>
  );
}
